import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { WebSocketAudioService } from './web-socket-audio.service';
import { NotifyAudioList } from 'src/app/constants/soundList';
import { HttpGuardService } from 'src/app/guards/http-guard.service';
import { ApiConstants } from 'src/app/constants/api-constants';
import { ServicecallService } from 'src/app/services/servicecall.service';
import { DataService } from 'src/app/services/data.service';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {

  private socket$!: WebSocketSubject<any>;
  private subscriptions: Subscription[];
  isActive$: BehaviorSubject<boolean>;
  isReconnecting$: BehaviorSubject<boolean>;
  isLogout: boolean;
  webSocketReconnectTimeoutInMsec = 2000;
  socketUrl: string = '';
  subscription: Subscription;
  isSocketSoundActive: boolean = true;

  constructor(
    private toastrService: ToastrService,
    private audioService: WebSocketAudioService,
    private httpGuard: HttpGuardService,
    private sharedService: ServicecallService,
    private dataSer: DataService,
    public jwtHelper: JwtHelperService
  ) {
    this.isActive$ = new BehaviorSubject<boolean>(false);
    this.isReconnecting$ = new BehaviorSubject<boolean>(false);
    this.subscriptions = [];
    this.isLogout = false;
    this.subscription = this.dataSer.events.subscribe((res: any) => {
      if (res.name === 'socketSound') {
        this.isSocketSoundActive = res?.args?.length ? res?.args[0] : '';
      } else if (res.name === 'socketDisconnected') {
        const isDisconnected = res?.args?.length ? res?.args[0] : '';
        isDisconnected ? this.logoutAndClose() : this.reconnect();
      } else if (res.name === 'socketDisconnectedOnLogout') {
        this.logoutAndClose();
      }
    });
  }

  connection(): WebSocketSubject<any> {
    // if (!this.socket$ || this.socket$.closed) {
    // this.socket$ = this.getNewWebSocket(environment.WebSocketUrl);
    this.socket$ = this.getNewWebSocket(this.socketUrl);
    // }
    return this.socket$;
  }

  private getNewWebSocket(url: string) {
    return webSocket({
      url: url,
      openObserver: {
        next: () => {
          this.isActive$.next(true);
        }
      },
      closeObserver: {
        next: () => {
          console.log("websocket closed");
          this.isActive$.next(false);
          if (this.isLogout == false) {
            this.isReconnecting$.next(true);
            // this.subscriptions.push(timer(this.webSocketReconnectTimeoutInMsec).subscribe(val => {
            this.reconnect();
            // }));
          }
        }
      },
    });
  }

  sendMessage(msg: any) {
    this.socket$.next(msg);
  }

  close() {
    this.socket$.complete();
  }

  logoutAndClose() {
    clearTimeout(undefined);
    this.isLogout = true;
    this.close();
  }

  connect() {
    this.isLogout = false;
    this.subscriptions.push(this.connection().subscribe({
      next: (data: any) => {
        const tenantId = localStorage.getItem('tenantId');
        const mindTenantId = data?.tenantId;
        if (tenantId === mindTenantId) {
          const { type, message, sourceTime } = data;
          this.sharedService.pushNotification(data);
          this.dataSer.broadcast('newNotification');
          if (message) {
            this.showNotificationBasedOnType(type, message);
          }
          this.isReconnecting$.next(false);
        }
      },
      error: () => {
        this.sharedService.toasterService({ type: 'error', msg: 'Unable to connect to web socket' });
        this.logoutAndClose();
        this.isReconnecting$.next(false);
      }
    }));
  }

  reconnect() {
    this.removeSubscriptions();
    this.getPubsubToken();
  }

  private removeSubscriptions() {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  showNotificationBasedOnType(type: string, message: string) {
    this.toastrService.info(message, type,
      {
        positionClass: 'toast-bottom-right',
        toastClass: 'toast-icon'
      });
    if (this.isSocketSoundActive)
      this.audioService.playSound(NotifyAudioList?.notification);
  }

  getPubsubToken() {
    this.socketUrl = '';
    this.httpGuard.httpReq('post', 'API_ENDPOINT', ApiConstants.URL.getPubsubToken, {}, true).subscribe((result: any) => {
      if (result['status']) {
        if (Object.keys(result['response']['data'])?.length) {
          this.socketUrl = result['response']['data']['url'];
        } else {
          this.sharedService.toasterService({ type: 'error', msg: 'Socket token unavailable.' });
        }
      } else {
        this.sharedService.toasterService({ type: 'error', msg: result?.response?.message });
      }
      this.connect();
      this.getTokenExpiry();
    });
  }

  getTokenExpiry() {
    let refreshTimer = null;
    if (this.socketUrl) {
      const pubSubToken = this.socketUrl.slice(this.socketUrl.indexOf("token"), this.socketUrl.length);
      const startTime = this.jwtHelper.decodeToken(pubSubToken)?.iat;
      const expTime = this.jwtHelper.decodeToken(pubSubToken)?.exp;
      if (expTime && startTime) {
        refreshTimer = (expTime - startTime) * 1000;
        setTimeout(() => {
          this.close();
        }, refreshTimer);
      }
    }
  }
}
